import { useNavigate } from 'react-router-dom'

import { useRaiseInvoice } from '@/features/finance/api/useInvoices'
import {
  INVOICE_CURRENCIES,
  today,
  type InvoiceCurrency,
} from '@/features/finance/invoice'

/* Raising a client invoice, and landing on it.
 *
 * ── A hook, because raising is not a form ──────────────────────────────────
 *
 * Nothing is asked before the POST. The server copies the client, the codes and
 * the shipment block from the case (AC-2), resolves the FX rate from the
 * economic date (AD-20) and prefills the charges from the accepted quote
 * revision (AD-15). The currency is the one fact the create genuinely freezes
 * (AD-9) and it is taken from the rate the deal was agreed at — a case priced in
 * USD invoiced in EUR is a mistake, not a choice. So the draft opens on its own
 * page, where the charges, the AD-10 ceiling and the AD-9 booking are in view
 * together, instead of behind a modal asking four questions with defensible
 * answers.
 *
 * ── Why it is a hook rather than a handler on `NewDocumentMenu` ────────────
 *
 * Two controls raise the same draft: that menu's "Client invoice" item, and the
 * "Raise invoice" button on every uninvoiced row of the payment schedule. The
 * only difference between them is whether an instalment is named — and a second
 * copy of the payload would be a second chance for one of them to stop sending
 * `trancheId`, which is precisely the defect that had every invoice this
 * application raised displacing no tranche at all.
 */

/** Opens a client invoice draft against this case and navigates to it. */
export function useRaiseInvoiceDraft(caseId: string, currency?: string) {
  const navigate = useNavigate()
  const raise = useRaiseInvoice(caseId)

  return {
    isPending: raise.isPending,
    isError: raise.isError,
    error: raise.error,
    reset: raise.reset,
    /** Opens the draft against `trancheId` — `''` for one outside the payment
     *  series, which is the ordinary value rather than a blank waiting to be
     *  filled (see `NewInvoicePayload`). */
    raise(trancheId: string) {
      if (raise.isPending) return
      raise.mutate(
        {
          currency: printableCurrency(currency),
          invoiceDate: today(),
          economicDate: today(),
          customerReference: '',
          trancheId,
          vatApplies: false,
          vatRate: '',
        },
        {
          /* Navigated from `onSuccess` and never optimistically: the invoice
             does not exist until the server has minted the row, booked the rate
             and prefilled the lines, and a route pushed ahead of that answer
             would land on a 404 that reads as a deleted record. A refusal
             leaves the reader where they are, with the sentence beside the
             button they pressed. */
          onSuccess: (invoice) =>
            navigate(`/cases/${caseId}/invoices/${invoice.id}`),
        },
      )
    },
  }
}

/** The document currency, from the currency the deal was agreed in.
 *
 *  Narrowed to the three an invoice may print in (AC-6, FR-61) rather than
 *  passed through: a rate agreed in TMT is a real case, and `finance.PrintsIn`
 *  would refuse the create outright — so an unprintable currency falls back to
 *  USD, which is the unit the ceiling is booked in anyway, and the invoice page
 *  states what was frozen. */
function printableCurrency(currency: string | undefined): InvoiceCurrency {
  return currency !== undefined &&
    (INVOICE_CURRENCIES as readonly string[]).includes(currency)
    ? (currency as InvoiceCurrency)
    : 'USD'
}
